import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import API_ENDPOINTS from '../../../Api/endpoints';
import {
  mockCustomerConfigs,
  mockOrganizations,
  mockGateways,
} from '../../../mockData/paymentGateways';

const CustomerConfigPage = () => {
  const auth = useSelector((state) => state.auth);
  const token = auth?.user?.token || auth?.token || '';

  const [configs, setConfigs] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // TODO: Replace with actual API calls in Backend Phase
      // const [configsRes, orgsRes, gatewaysRes] = await Promise.all([
      //   axios.get(API_ENDPOINTS.PAYMENT_GATEWAYS.CUSTOMER_CONFIG.LIST, { headers: { Authorization: `Bearer ${token}` } }),
      //   axios.get(API_ENDPOINTS.ORGANISATIONS.LIST, { headers: { Authorization: `Bearer ${token}` } }),
      //   axios.get(API_ENDPOINTS.PAYMENT_GATEWAYS.LIST, { headers: { Authorization: `Bearer ${token}` } }),
      // ]);
      // setConfigs(configsRes.data);
      // setOrganizations(orgsRes.data);
      // setGateways(gatewaysRes.data);

      // For now, use mock data
      setConfigs(mockCustomerConfigs);
      setOrganizations(mockOrganizations);
      setGateways(mockGateways.filter(g => g.isActive));
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredConfigs = selectedOrg
    ? configs.filter(c => c.organizationId === selectedOrg)
    : configs;

  const handleEnableGateway = async () => {
    if (!selectedOrg || !selectedGateway) {
      setError('Please select both organization and gateway');
      return;
    }

    try {
      // TODO: Replace with actual API call in Backend Phase
      // await axios.post(API_ENDPOINTS.PAYMENT_GATEWAYS.CUSTOMER_CONFIG.ENABLE, {
      //   organizationId: selectedOrg,
      //   gatewayId: selectedGateway,
      // }, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });

      // Mock implementation
      const org = organizations.find(o => o.id === selectedOrg);
      const gateway = gateways.find(g => g.id === selectedGateway);

      const newConfig = {
        id: `config-${Date.now()}`,
        organizationId: selectedOrg,
        organizationName: org.name,
        gatewayId: selectedGateway,
        gatewayName: gateway.gatewayName,
        isEnabled: true,
        enabledAt: new Date().toISOString(),
        enabledBy: 'Super Admin',
      };

      setConfigs([...configs, newConfig]);
      setSuccess(`${gateway.gatewayName} enabled for ${org.name}`);
      setOpenDialog(false);
      setSelectedGateway('');
    } catch (err) {
      setError('Failed to enable gateway');
      console.error(err);
    }
  };

  const handleDisableGateway = async (configId) => {
    try {
      // TODO: Replace with actual API call in Backend Phase
      // await axios.post(API_ENDPOINTS.PAYMENT_GATEWAYS.CUSTOMER_CONFIG.DISABLE(configId), {}, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });

      // Mock implementation
      setConfigs(configs.filter(c => c.id !== configId));
      setSuccess('Gateway disabled successfully');
    } catch (err) {
      setError('Failed to disable gateway');
      console.error(err);
    }
  };

  const getAvailableGateways = () => {
    if (!selectedOrg) return [];

    const enabledGatewayIds = configs
      .filter(c => c.organizationId === selectedOrg && c.isEnabled)
      .map(c => c.gatewayId);

    return gateways.filter(g => !enabledGatewayIds.includes(g.id));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Customer Payment Gateway Configuration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Filter by Organization</InputLabel>
                <Select
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  label="Filter by Organization"
                >
                  <MenuItem value="">All Organizations</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
                disabled={!selectedOrg}
                fullWidth
              >
                Enable Gateway for Organization
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Gateway</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enabled At</TableCell>
                  <TableCell>Enabled By</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConfigs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No gateway configurations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConfigs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell>{config.organizationName}</TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {config.gatewayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.isEnabled ? 'Enabled' : 'Disabled'}
                          color={config.isEnabled ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {config.enabledAt
                          ? new Date(config.enabledAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{config.enabledBy || '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDisableGateway(config.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Enable Gateway Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enable Payment Gateway</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Organization: {organizations.find(o => o.id === selectedOrg)?.name}
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select Gateway</InputLabel>
              <Select
                value={selectedGateway}
                onChange={(e) => setSelectedGateway(e.target.value)}
                label="Select Gateway"
              >
                {getAvailableGateways().map((gateway) => (
                  <MenuItem key={gateway.id} value={gateway.id}>
                    {gateway.gatewayName} - {gateway.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEnableGateway}
            variant="contained"
            disabled={!selectedGateway}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerConfigPage;
